


import { Component, Vue, Watch } from "vue-property-decorator";

import { Customers } from "App";
import { Customer } from "App/Models";
import { GroupUser } from "App/Contracts";
import { EventBus } from "App/IOC";

import LoadingDots from "Web/Components/Common/LoadingDots.vue";
import { CustomValue } from "@Internal/Models";
import { Option } from "Web/Contracts";
import SimpleSelect from "Web/Components/V2/Select/Simple.vue";

@Component({
    components: {
        LoadingDots,
        SimpleSelect
    }
})
export default class CustomerGroupUsersView extends Vue {
    private customer: Customer = { Id: -1, Name: "" };

    private users: GroupUser[] = [];
    private loading: boolean = true;

    /* Filtres client-side : l'ensemble (un groupe) est petit, on filtre en mémoire. */
    private statusFilter: string = "";

    private keyword: string = "";

    /* Pagination client-side. */
    private readonly pageSize: number = 15;
    private page: number = 1;

    /* La liste du statut au composant du DS ( 26/09/2026 ), comme l'ecran
       Utilisateurs. « Tous » porte l'identifiant `all` : un identifiant vide se
       confondrait avec « rien de choisi ». */
    private get statusOptions(): Option[] {
        return [
            new CustomValue<string>( this.$t( "CUSTOMERS_userFilterAll" ) as string, "all" ),
            new CustomValue<string>( this.$t( "CUSTOMERS_userStatusActive" ) as string, "enabled" ),
            new CustomValue<string>(
                this.$t( "CUSTOMERS_userStatusProcessing" ) as string, "pending"
            ),
            new CustomValue<string>(
                this.$t( "CUSTOMERS_userStatusInactive" ) as string, "disabled"
            )
        ];
    }

    private get selectedStatus(): Option | undefined {
        const id = this.statusFilter === "" ? "all" : this.statusFilter;

        return this.statusOptions.find( ( option: Option ) => option.Id === id );
    }

    private selectStatus( option: Option ): void {
        this.statusFilter = option.Id === "all" ? "" : String( option.Id );
    }

    private created(): void {
        this.setup();
    }

    /* Tout changement de filtre ramène à la première page. */
    @Watch( "statusFilter" )
    @Watch( "keyword" )
    private onFilterChange(): void {
        this.page = 1;
    }

    @Watch( "$route" )
    private onRouteChange(): void {
        this.setup();
    }

    private async setup(): Promise<void> {
        this.loading = true;

        const id = Number( this.$route.params.id );

        const [ customer, customerError ] = await Customers.Get( id );

        if( customerError !== undefined || customer === undefined ) {
            this.loading = false;
            EventBus.Publish( "Error", customerError );
            return;
        }

        this.customer = customer as Customer;
        document.title = this.customer.Company || this.customer.Name || "";

        const [ users, error ] = await Customers.ListGroupUsers( id );

        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.users = users;
    }

    private get filteredUsers(): GroupUser[] {
        const keyword = this.keyword.trim().toLowerCase();

        return this.users.filter( ( user: GroupUser ): boolean => {
            if( this.statusFilter !== "" && user.Status !== this.statusFilter )
                return false;

            if( keyword === "" )
                return true;

            return ( user.Name || "" ).toLowerCase().includes( keyword ) ||
                ( user.Email || "" ).toLowerCase().includes( keyword );
        });
    }

    private get totalPages(): number {
        return Math.max( 1, Math.ceil( this.filteredUsers.length / this.pageSize ) );
    }

    private get paginatedUsers(): GroupUser[] {
        const start = ( this.page - 1 ) * this.pageSize;

        return this.filteredUsers.slice( start, start + this.pageSize );
    }

    private previousPage(): void {
        if( this.page > 1 )
            this.page--;
    }

    private nextPage(): void {
        if( this.page < this.totalPages )
            this.page++;
    }

    private statusLabel( status: string ): string {
        switch( status ) {
            case "enabled":
                return this.$t( "CUSTOMERS_userStatusActive" ) as string;

            case "pending":
                return this.$t( "CUSTOMERS_userStatusProcessing" ) as string;

            case "disabled":
                return this.$t( "CUSTOMERS_userStatusInactive" ) as string;

            default:
                return "—";
        }
    }

    /* Map the endpoint status (enabled/pending/disabled) onto the existing visual classes
       (active/processing/inactive) so the colours stay unchanged. */
    private statusClass( status: string ): string {
        let variant = "unknown";

        switch( status ) {
            case "enabled":
                variant = "active";
                break;

            case "pending":
                variant = "processing";
                break;

            case "disabled":
                variant = "inactive";
                break;
        }

        return `c-group-users__status--${ variant }`;
    }

    private formatDate( value: string ): string {
        if( !value )
            return "—";

        const date = new Date( value );

        if( isNaN( date.getTime() ) )
            return "—";

        return date.toLocaleDateString( "fr-FR" );
    }

    private goBack(): void {
        this.$router.push( `/customers/${ this.customer.Id }` );
    }
}
